
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { Invoice } from '@/types/payments';
import { userFacingErrorMessage } from '@/utils/userFacingError';

export function useInvoiceBulkActions(
  invoices: Invoice[],
  selectedInvoices: string[],
  setPaymentDialogOpen: (open: boolean) => void,
  setCancelDialogOpen: (open: boolean) => void
) {
  const t = useTranslations('consultantToasts');

  const handleBulkPayment = () => {
    if (selectedInvoices.length === 0) {
      toast.warning(t('bulk.noneSelectedTitle'), { description: t('bulk.noneSelectedPayment') });
      return;
    }
    setPaymentDialogOpen(true);
  };

  const handleBulkCancel = () => {
    if (selectedInvoices.length === 0) {
      toast.warning(t('bulk.noneSelectedTitle'), { description: t('bulk.noneSelectedCancellation') });
      return;
    }
    setCancelDialogOpen(true);
  };

  const handleBulkExport = () => {
    if (selectedInvoices.length === 0) {
      toast.warning(t('bulk.noneSelectedTitle'), { description: t('bulk.noneSelectedExport') });
      return;
    }

    try {
      // Simulate exporting the selected invoices to a file (e.g., CSV, PDF)
      const selectedInvoiceObjects = invoices.filter(invoice => selectedInvoices.includes(invoice.id));
      const exportData = JSON.stringify(selectedInvoiceObjects, null, 2);
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'selected-invoices.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(t('bulk.exportedTitle'), {
        description: t('bulk.exportedBody', { count: selectedInvoices.length }),
      });
    } catch (error: any) {
      console.error("Error exporting invoices:", error);
      toast.error(t('common.error'), { description: userFacingErrorMessage(error, t('bulk.exportFailed')) });
    }
  };

  return {
    handleBulkPayment,
    handleBulkCancel,
    handleBulkExport
  };
}
